import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Buildings, Tag, BookOpenText, Compass, FireSimple } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import { resolveImageUrl } from "@/lib/api";

const GENRE_COLORS = [
  "#E63946", "#7209B7", "#10B981", "#F59E0B", "#3B82F6", "#EC4899",
  "#8B5CF6", "#14B8A6", "#EF4444", "#6366F1", "#F97316", "#06B6D4",
];

export default function Explorar() {
  const [genres, setGenres] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/genres").then(r => setGenres(r.data)).catch(() => {}),
      api.get("/publishers").then(r => setPublishers(r.data)).catch(() => {}),
      api.get("/manga/popular", { params: { limit: 6 } }).then(r => setPopular(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const maxGenre = genres.length ? genres[0].count : 1;

  return (
    <div className="pt-20 max-w-7xl mx-auto px-5 lg:px-8 pb-24" data-testid="explorar-page">
      <Seo title="Explorar" description="Descobre mangás por género e por editora — encontra a tua próxima leitura." path="/explorar" />

      <div className="mb-10">
        <div className="font-mono-accent text-xs text-[#E63946] mb-2 flex items-center gap-2">
          <Compass size={14} weight="bold" /> EXPLORAR
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">Descobre mangás</h1>
        <p className="text-zinc-400 mt-2 max-w-xl">Navega por género ou editora e encontra a tua próxima leitura.</p>
      </div>

      {loading ? (
        <div className="text-center text-zinc-500 py-20 font-mono-accent text-xs">A CARREGAR...</div>
      ) : (
        <div className="space-y-14">
          {/* GÉNEROS */}
          <section data-testid="explore-genres">
            <div className="flex items-center gap-2 mb-5">
              <Tag size={18} weight="fill" className="text-[#E63946]" />
              <h2 className="font-display text-2xl font-bold">Por Género</h2>
            </div>
            {genres.length === 0 ? (
              <div className="text-sm text-zinc-500 font-mono-accent">SEM GÉNEROS AINDA</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {genres.map((g, i) => {
                  const color = GENRE_COLORS[i % GENRE_COLORS.length];
                  const pct = Math.max(12, Math.round((g.count / maxGenre) * 100));
                  return (
                    <Link
                      key={g.genre}
                      to={`/database?q=${encodeURIComponent(g.genre)}`}
                      className="group relative overflow-hidden rounded-xl border border-white/10 bg-[#121217] p-4 hover:border-white/20 transition-colors"
                      data-testid={`explore-genre-${g.genre}`}
                    >
                      <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity" style={{ background: color }} />
                      <div className="relative">
                        <div className="flex items-center justify-between">
                          <span className="font-display font-semibold truncate">{g.genre}</span>
                          <span className="text-xs font-mono-accent text-zinc-400">{g.count}</span>
                        </div>
                        <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>

          {/* EDITORAS */}
          <section data-testid="explore-publishers">
            <div className="flex items-center gap-2 mb-5">
              <Buildings size={18} weight="fill" className="text-[#7209B7]" />
              <h2 className="font-display text-2xl font-bold">Por Editora</h2>
            </div>
            {publishers.length === 0 ? (
              <div className="text-sm text-zinc-500 font-mono-accent">SEM EDITORAS AINDA</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {publishers.map((p) => (
                  <Link
                    key={p.publisher_id}
                    to={`/editoras/${p.publisher_id}`}
                    className="group flex items-center gap-3 rounded-xl border border-white/10 bg-[#121217] p-4 hover:border-[#7209B7]/40 transition-colors"
                    data-testid={`explore-publisher-${p.publisher_id}`}
                  >
                    {p.image_url ? (
                      <img src={resolveImageUrl(p.image_url)} alt={p.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#7209B7] to-[#E63946] grid place-items-center flex-shrink-0">
                        <Buildings size={20} weight="fill" className="text-white" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-display font-semibold truncate group-hover:text-[#7209B7] transition-colors">{p.name}</div>
                      {p.founded_year && <div className="text-[11px] text-zinc-500 font-mono-accent">Desde {p.founded_year}</div>}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* POPULARES */}
          {popular.length > 0 && (
            <section data-testid="explore-popular">
              <div className="flex items-center gap-2 mb-5">
                <FireSimple size={18} weight="fill" className="text-amber-400" />
                <h2 className="font-display text-2xl font-bold">Mais Vistos</h2>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-5">
                {popular.map((m) => (
                  <Link key={m.manga_id} to={`/manga/${m.slug || m.manga_id}`} className="group" data-testid={`explore-popular-${m.manga_id}`}>
                    <div className="aspect-[2/3] overflow-hidden rounded-xl bg-[#121217] border border-white/5">
                      {m.cover_url ? (
                        <img src={m.cover_url} alt={m.title} className="manga-cover w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-zinc-700"><BookOpenText size={24} weight="duotone" /></div>
                      )}
                    </div>
                    <div className="mt-2 font-display font-semibold text-sm leading-tight line-clamp-2 group-hover:text-[#E63946] transition-colors">{m.title}</div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
